import React, { useState } from "react";
import {
  Box, Typography, Paper, Button, useMediaQuery,
  Snackbar, Alert, Container, TextField, MenuItem,
  Grid, InputAdornment
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth } from "../../context/AuthContext";

// Hooks y Utils
import { useGestorUsuarios } from "./hooks/useGestorUsuarios";
import { normalizarRol } from "./utils/formatters";

// Componentes Refactorizados
import AssignInstitutionDialog from "./components/AssignInstitutionDialog";
import DeleteConfirmDialog from "./components/DeleteConfirmDialog";
import DetalleListaDialog from "./components/DetalleListaDialog";
import TemporaryPasswordDialog from "./components/TemporaryPasswordDialog";
import UserFormDialog from "./components/UserFormDialog";
import UserTable from "./components/UserTable";
import ActionConfirmDialog from "./components/ActionConfirmDialog";
import CreateInstitutionDialog from "./components/CreateInstitutionDialog";
import HeroHeader from "../HeroHeader";
import get_user from "../../assets/get_user.png"

const GestorUsuarios = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();

  // Estado de paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: "",
    rolId: "",
    institucionId: "",
    nivelModalidad: "",
    fechaDesde: "",
    fechaHasta: "",
  });
  const [sort, setSort] = useState({
    orderBy: "created_at",
    orderDir: "desc",
  });

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = Number(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Regresamos a la página 1 cuando se cambia la cantidad
  };

  // Hook Principal
  const {
    usuarios,
    roles,
    totalUsuarios,
    institucionesFiltro,
    ubicaciones,
    loading,
    snackbar,
    setSnackbar,
    acciones
  } = useGestorUsuarios({ page, rowsPerPage, filters, sort });

  // Estados locales para controlar qué diálogo está abierto
  const [dialogs, setDialogs] = useState({
    create: false,
    edit: false,
    delete: false,
    assign: false,
    createInstitution: false,
    viewMore: false,
    tempPassword: false,
  });

  // Estado para el usuario que se está manipulando
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({ nombre: "", apellido: "", usuario: "" });
  const initialAssignState = {
    usuario_id: "",
    original_institucion_id: "",
    rol_id: "",
    rol_nombre: "",
    region_id: "",
    provincia_id: "",
    distrito_id: "",
    institucion_id: ""
  };
  const [assignData, setAssignData] = useState(initialAssignState);
  const initialInstitutionState = {
    id: "",
    nombre: "",
    codigo_institucion: "",
    nivel_modalidad: "",
    tipo_gestion: "",
    direccion: "",
    region_id: "",
    provincia_id: "",
    distrito_id: "",
  };
  const [institutionData, setInstitutionData] = useState(initialInstitutionState);
  const [moreData, setMoreData] = useState({ title: "", items: [] });
  const [tempPasswordData, setTempPasswordData] = useState({
    title: "",
    description: "",
    password: "",
    userId: "",
  });
  const [assignMode, setAssignMode] = useState("create");
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    description: "",
    confirmLabel: "Confirmar",
    confirmColor: "#d9534f",
    onConfirm: null,
  });

  const initialFormState = {
    id: "",
    nombre: "",
    apellido: "",
    usuario: ""
  };

  const handleOpenViewMore = (title, items) => {
    setMoreData({ title, items });
    setDialogs(prev => ({ ...prev, viewMore: true }));
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => {
      const next = { ...prev, [name]: value };

      if (name === "rolId") {
        const rolSeleccionado = roles.find((rol) => String(rol.id) === String(value));
        const rolNombre = normalizarRol(rolSeleccionado?.nombre);

        if (rolNombre === "admin") {
          next.nivelModalidad = "";
          next.institucionId = "";
        }
      }

      if (name === "nivelModalidad") {
        next.institucionId = "";
      }

      return next;
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      rolId: "",
      institucionId: "",
      nivelModalidad: "",
      fechaDesde: "",
      fechaHasta: "",
    });
    setSort({
      orderBy: "created_at",
      orderDir: "desc",
    });
    setPage(0);
  };

  const handleSort = (field) => {
    setSort(prev => {
      if (prev.orderBy === field) {
        return {
          ...prev,
          orderDir: prev.orderDir === "asc" ? "desc" : "asc",
        };
      }

      return {
        orderBy: field,
        orderDir: field === "created_at" ? "desc" : "asc",
      };
    });
    setPage(0);
  };


  // --- Handlers de Usuario ---

  // Función para cerrar y limpiar
  const handleCloseDialogs = () => {
    // 1. Primero cerramos los diálogos para que inicie la animación
    setDialogs({
      create: false,
        edit: false,
        delete: false,
        assign: false,
        createInstitution: false,
        viewMore: false,
        tempPassword: false,
      });

    // 2. Esperamos 300ms (lo que dura la transición de MUI) para limpiar los datos
    setTimeout(() => {
      setFormData(initialFormState);
      setAssignData(initialAssignState);
      setInstitutionData(initialInstitutionState);
      setSelectedUser(null);
      setAssignMode("create");
    }, 300);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
  };
  // Al abrir el de creación, aseguramos que esté limpio
  const handleOpenCreate = () => {
    setFormData(initialFormState);
    setDialogs(prev => ({ ...prev, create: true }));
  };

  const handleOpenCreateInstitution = async () => {
    setInstitutionData(initialInstitutionState);
    setDialogs(prev => ({ ...prev, createInstitution: true }));
    await acciones.cargarRegiones();
  };

  // Al abrir el de edición, cargamos los datos del usuario
  const handleOpenEdit = (user) => {
    setSelectedUser(user);
    setAssignMode("create");
    setFormData({
      id: user.id || user.usuario,
      nombre: user.nombres || user.nombre || "",
      apellido: user.apellidos || user.apellido || "",
      usuario: user.id || user.usuario || ""
    });
    setDialogs(prev => ({ ...prev, edit: true }));
  };

  const handleOpenAssign = async (user) => {
    setSelectedUser(user);
    setAssignMode("create");
    setAssignData({ ...initialAssignState, usuario_id: user.id });
    setDialogs(prev => ({ ...prev, assign: true }));
    await acciones.cargarRegiones();
  };

  const handleOpenEditVinculo = async (vinculo) => {
    const ubicacionId = vinculo?.institucion?.ubicacion_id || "";
    const regionId = ubicacionId.slice(0, 2);
    const provinciaId = ubicacionId.slice(0, 4);
    const distritoId = ubicacionId.slice(0, 6);

    setAssignMode("edit");
    setAssignData({
      usuario_id: selectedUser?.id || formData.id,
      original_institucion_id: vinculo.institucion_id,
      rol_id: String(vinculo.rol_id ?? ""),
      rol_nombre: vinculo.rol?.nombre || "",
      region_id: regionId,
      provincia_id: provinciaId,
      distrito_id: distritoId,
      institucion_id: vinculo.institucion_id,
    });
    setDialogs(prev => ({ ...prev, assign: true }));

    await acciones.cargarRegiones();
    if (regionId) await acciones.cargarProvincias(regionId);
    if (provinciaId) await acciones.cargarDistritos(provinciaId);
    if (distritoId) await acciones.cargarInstituciones(distritoId);
  };

  const handleDelete = async () => {
    const success = await acciones.eliminarUsuario(selectedUser.id);
    if (success) setDialogs({ ...dialogs, delete: false });
  };

  const handleSave = async () => {
    // Validación básica antes de enviar al backend
    if (!formData.usuario || !formData.nombre || !formData.apellido) {
      acciones.showMsg("Por favor, completa todos los campos requeridos.", "warning");
      return;
    }

    if (!dialogs.edit && formData.usuario.length !== 8) {
      acciones.showMsg("El DNI debe tener exactamente 8 dígitos.", "warning");
      return;
    }

    const result = await acciones.guardarUsuario(formData, dialogs.edit);
    if (!result?.success) return;

    handleCloseDialogs();

    if (!dialogs.edit && result.data?.temporary_password) {
      setTempPasswordData({
        title: "Contraseña inicial asignada",
        description: "La contraseña inicial del usuario es su propio DNI. Compártela por correo para el primer acceso.",
        password: result.data.temporary_password,
        userId: result.data.usuario?.id || formData.usuario,
      });
      setDialogs(prev => ({ ...prev, tempPassword: true }));
    }
  };

  const handleResetPassword = async (usuario) => {
    setConfirmDialog({
      open: true,
      title: "Confirmar restablecimiento de contraseña",
      description: `La contraseña de ${usuario.nombres.split(" ")[0]} ${usuario.apellidos.split(" ")[0]} se restablecerá a su propio DNI. La contraseña actual dejará de ser válida inmediatamente.`,
      confirmLabel: "Restablecer al DNI",
      confirmColor: "#d97706",
      onConfirm: async () => {
        const result = await acciones.resetearPasswordUsuario(usuario);
        if (!result?.success) return;

        handleCloseConfirmDialog();
        setTempPasswordData({
          title: "Contraseña restablecida",
          description: "La contraseña del usuario volvió a ser su propio DNI. Comparte ese dato si corresponde.",
          password: result.data?.temporary_password,
          userId: result.data?.usuario_id || usuario.id,
        });
        setDialogs(prev => ({ ...prev, tempPassword: true }));
      },
    });
  };

  const handleOpenUnlinkVinculo = (vinculo) => {
    const nombreInstitucion = vinculo?.institucion?.nombre || "esta institución";
    const nivel = vinculo?.institucion?.nivel_modalidad || "sin nivel";
    const userId = selectedUser?.id || formData.id;
    const vinculosRestantes = Array.isArray(selectedUser?.vinculos)
      ? selectedUser.vinculos.length - 1
      : 0;

    setConfirmDialog({
      open: true,
      title: "Confirmar desvinculación",
      description:
        vinculosRestantes <= 0
          ? `Se eliminará el vínculo de ${userId} con ${nombreInstitucion} (${nivel}). El usuario quedará sin instituciones asociadas y no podrá trabajar con contextos institucionales hasta recibir una nueva asignación.`
          : `Se eliminará el vínculo de ${userId} con ${nombreInstitucion} (${nivel}). Esta acción cerrará las sesiones activas asociadas a esa institución.`,
      confirmLabel: "Desvincular institución",
      confirmColor: "#d9534f",
      onConfirm: async () => {
        const success = await acciones.desvincularInstitucionUsuario(
          userId,
          vinculo.institucion_id
        );
        if (!success) return;

        setSelectedUser((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            vinculos: (prev.vinculos || []).filter(
              (item) => item.institucion_id !== vinculo.institucion_id
            ),
          };
        });
        handleCloseConfirmDialog();
      },
    });
  };

  const handleAssignSave = async () => {
    const success = assignMode === "edit"
      ? await acciones.actualizarVinculoInstitucion(assignData)
      : await acciones.asignarInstitucion(assignData);
    if (success) handleCloseDialogs();
  };

  const handleSaveInstitution = async () => {
    if (!institutionData.id || !institutionData.nombre || !institutionData.nivel_modalidad || !institutionData.distrito_id) {
      acciones.showMsg("Completa el código modular, nombre, nivel/modalidad y distrito.", "warning");
      return;
    }

    if (!/^\d{7}$/.test(institutionData.id)) {
      acciones.showMsg("El código modular debe tener exactamente 7 dígitos.", "warning");
      return;
    }

    const result = await acciones.crearInstitucion({
      id: institutionData.id,
      nombre: institutionData.nombre.trim(),
      codigo_institucion: institutionData.codigo_institucion.trim() || null,
      nivel_modalidad: institutionData.nivel_modalidad.trim(),
      tipo_gestion: institutionData.tipo_gestion.trim() || null,
      direccion: institutionData.direccion.trim() || null,
      ubicacion_id: institutionData.distrito_id,
    });

    if (result?.success) {
      handleCloseDialogs();
    }
  };
  // --- Handlers de Formularios ---
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAssignChange = async (e) => {
    const { name, value } = e.target;
    const normalizedValue = name === "rol_id" ? String(value) : value;

    setAssignData(prev => {
      const next = { ...prev, [name]: normalizedValue };

      if (name === "region_id") {
        next.provincia_id = "";
        next.distrito_id = "";
        next.institucion_id = "";
      }

      if (name === "provincia_id") {
        next.distrito_id = "";
        next.institucion_id = "";
      }

      if (name === "distrito_id") {
        next.institucion_id = "";
      }

      return next;
    });

    // Lógica de carga en cascada
    if (name === "region_id") await acciones.cargarProvincias(value);
    if (name === "provincia_id") await acciones.cargarDistritos(value);
    if (name === "distrito_id") await acciones.cargarInstituciones(value);
    if (name === "rol_id") {
      const rol = roles.find(r => String(r.id) === String(value));
      setAssignData(prev => ({ ...prev, rol_id: String(value), rol_nombre: rol?.nombre || "" }));
    }
  };

  const handleInstitutionChange = async (e) => {
    const { name, value } = e.target;

    setInstitutionData((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "id") {
        next.id = value.replace(/\D/g, "").slice(0, 7);
      }

      if (name === "region_id") {
        next.provincia_id = "";
        next.distrito_id = "";
      }

      if (name === "provincia_id") {
        next.distrito_id = "";
      }

      return next;
    });

    if (name === "region_id") await acciones.cargarProvincias(value);
    if (name === "provincia_id") await acciones.cargarDistritos(value);
  };

  const totalPages = totalUsuarios > 0 ? Math.ceil(totalUsuarios / rowsPerPage) : 0;
  const canGoNext = totalUsuarios > 0 && page + 1 < totalPages;
  const canManagePasswords = user?.rol?.nombre === "ADMIN";
  const canManageInstitutions = user?.rol?.nombre === "ADMIN";
  const hasActiveFilters = Boolean(
    filters.search ||
    filters.rolId ||
    filters.institucionId ||
    filters.nivelModalidad ||
    filters.fechaDesde ||
    filters.fechaHasta
  );
  const rolSeleccionado = roles.find((rol) => String(rol.id) === String(filters.rolId));
  const rolSeleccionadoNombre = normalizarRol(rolSeleccionado?.nombre);
  const deshabilitarFiltrosInstitucionales = rolSeleccionadoNombre === "admin";
  const nivelesDisponibles = Array.from(
    new Set(
      (institucionesFiltro || [])
        .map(inst => inst.nivel_modalidad)
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b));
  const institucionesDisponibles = (institucionesFiltro || [])
    .filter((inst) => {
      if (deshabilitarFiltrosInstitucionales) return false;
      if (!filters.nivelModalidad) return false;
      return inst.nivel_modalidad === filters.nivelModalidad;
    })
    .sort((a, b) => a.nombre.localeCompare(b.nombre));

  return (
    <Box>
      <Box sx={{ py: 4, px: { xs: 2, md: 4 } }}>

        <HeroHeader
          img={get_user}
          subtitle="MÓDULO GESTOR DE USUARIOS"
          title="Gestor de usuarios"
          description="Administra usuarios, asigna instituciones y gestiona sus accesos desde un solo lugar"
          maxWidth="xxl"
        />
      </Box>

      <Container maxWidth="xxl" >
        <Box sx={{ paddingInline: "4%" }} >
          <Paper elevation={0} sx={paperStyles}>
            {/* Toolbar */}
            <Box sx={toolbarStyles}>
              <Typography sx={tableTitleStyles}>Listado de usuarios</Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {canManageInstitutions && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreateInstitution}
                    sx={secondaryCreateBtnStyles}
                  >
                    Crear institución
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreate}
                  sx={secondaryCreateBtnStyles}
                >
                  Crear usuario
                </Button>
              </Box>
            </Box>

            {/* Tabla Refactorizada */}
            <Box sx={{ p: { xs: 1, md: 2 } }}>
              <Paper
                variant="outlined"
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  borderColor: "rgba(15,23,42,0.08)",
                  backgroundColor: "#f8fafc",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Buscar por nombres o apellidos"
                      name="search"
                      value={filters.search}
                      onChange={handleFilterChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Rol"
                      name="rolId"
                      value={filters.rolId}
                      onChange={handleFilterChange}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      {roles.map((rol) => (
                        <MenuItem key={rol.id} value={rol.id}>
                          {rol.nombre}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Nivel"
                      name="nivelModalidad"
                      value={filters.nivelModalidad}
                      onChange={handleFilterChange}
                      disabled={deshabilitarFiltrosInstitucionales}
                      helperText={deshabilitarFiltrosInstitucionales ? "No aplica para administrador" : "Filtra primero por nivel"}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      {nivelesDisponibles.map((nivel) => (
                        <MenuItem key={nivel} value={nivel}>
                          {nivel}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Institución educativa"
                      name="institucionId"
                      value={filters.institucionId}
                      onChange={handleFilterChange}
                      disabled={
                        loading.institucionesFiltro ||
                        deshabilitarFiltrosInstitucionales ||
                        !filters.nivelModalidad
                      }
                      helperText={
                        deshabilitarFiltrosInstitucionales
                          ? "No aplica para administrador"
                          : !filters.nivelModalidad
                            ? "Selecciona un nivel primero"
                            : "Instituciones del nivel seleccionado"
                      }
                    >
                      <MenuItem value="">Todas</MenuItem>
                      {institucionesDisponibles.map((inst) => (
                        <MenuItem key={inst.id} value={inst.id}>
                          {inst.nombre} ({inst.nivel_modalidad})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Desde"
                      name="fechaDesde"
                      value={filters.fechaDesde}
                      onChange={handleFilterChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Hasta"
                      name="fechaHasta"
                      value={filters.fechaHasta}
                      onChange={handleFilterChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Box sx={{ display: "flex", gap: 1, height: "100%", width: "100%", alignItems: "center", justifyContent: "flex-end" }}>
                      <Button
                        variant="outlined"
                        onClick={handleClearFilters}
                        sx={{ textTransform: "none", fontFamily: "Montserrat-Medium" }}
                      >
                        Limpiar filtros
                      </Button>
                      <Typography sx={{ fontFamily: "Montserrat-Medium", fontSize: "0.9rem", color: "#475569" }}>
                        {totalUsuarios} resultados
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              <UserTable
                usuarios={usuarios}
                loading={loading.usuarios}
                isMobile={isMobile}
                onEdit={handleOpenEdit}
                onDelete={(u) => { setSelectedUser(u); setDialogs({ ...dialogs, delete: true }); }}
                onAssign={handleOpenAssign}
                onResetPassword={handleResetPassword}
                onViewMore={handleOpenViewMore}
                sort={sort}
                onSort={handleSort}
                hasActiveFilters={hasActiveFilters}
                onClearFilters={handleClearFilters}
                canResetPassword={canManagePasswords}
              />

              {/* Paginación */}
              <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>

                {/* Lado izquierdo: Info de página y Selector de filas */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography sx={{ fontFamily: "Montserrat-Medium", fontSize: "0.9rem", color: "#475569" }}>
                    Página {totalPages === 0 ? 0 : page + 1} de {totalPages}
                  </Typography>

                  <TextField
                    select
                    size="small"
                    label="Mostrar"
                    value={rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                    disabled={loading.usuarios}
                    sx={{ minWidth: 130 }}
                  >
                    <MenuItem value={10}>10 usuarios</MenuItem>
                    <MenuItem value={20}>20 usuarios</MenuItem>
                    <MenuItem value={25}>25 usuarios</MenuItem>
                    <MenuItem value={50}>50 usuarios</MenuItem>
                  </TextField>
                </Box>

                {/* Lado derecho: Botones de navegación */}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0 || loading.usuarios}
                    sx={{ textTransform: "none", fontFamily: "Montserrat-Medium" }}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setPage(p => p + 1)}
                    disabled={loading.usuarios || !canGoNext}
                    sx={{
                      textTransform: "none",
                      fontFamily: "Montserrat-Medium",
                      backgroundColor: "#3857a6",
                      "&:hover": { backgroundColor: "#2f4c98" }
                    }}
                  >
                    Siguiente
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>

      </Container>

      {/* Diálogo para CREAR Usuario */}
      <UserFormDialog
        open={dialogs.create}
        isEdit={false}
        formData={formData}
        onChange={handleFormChange}
        onClose={handleCloseDialogs}
        onSave={handleSave}
      />

      {/* Diálogo para EDITAR Usuario */}
      <UserFormDialog
        open={dialogs.edit}
        isEdit={true}
        formData={formData}
        onChange={handleFormChange}
        onClose={handleCloseDialogs}
        onSave={handleSave}
        vinculos={selectedUser?.vinculos || []}
        onEditVinculo={handleOpenEditVinculo}
        onUnlinkVinculo={handleOpenUnlinkVinculo}
      />

      <AssignInstitutionDialog
        open={dialogs.assign}
        usuario={selectedUser}
        formData={assignData}
        rolesAsignables={roles.filter(r => ["director", "profesor"].includes(normalizarRol(r.nombre)))}
        regiones={ubicaciones.regiones}
        provincias={ubicaciones.provincias}
        distritos={ubicaciones.distritos}
        instituciones={ubicaciones.instituciones}
        loading={loading}
        onChange={handleAssignChange}
        onClose={() => setDialogs(prev => ({ ...prev, assign: false }))}
        onSave={handleAssignSave}
        canSave={!!assignData.rol_id && !!assignData.institucion_id}
        mode={assignMode}
      />

      <CreateInstitutionDialog
        open={dialogs.createInstitution}
        formData={institutionData}
        regiones={ubicaciones.regiones}
        provincias={ubicaciones.provincias}
        distritos={ubicaciones.distritos}
        loading={loading}
        canSave={
          !!institutionData.id &&
          !!institutionData.nombre.trim() &&
          !!institutionData.nivel_modalidad.trim() &&
          !!institutionData.distrito_id
        }
        onChange={handleInstitutionChange}
        onClose={handleCloseDialogs}
        onSave={handleSaveInstitution}
      />

      <DeleteConfirmDialog
        open={dialogs.delete}
        usuarioId={selectedUser?.id}
        onClose={() => setDialogs({ ...dialogs, delete: false })}
        onConfirm={handleDelete}
      />

      {/* Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
      <DetalleListaDialog
        open={dialogs.viewMore}
        onClose={() => setDialogs(prev => ({ ...prev, viewMore: false }))}
        titulo={moreData.title}
        items={moreData.items}
      />
      <TemporaryPasswordDialog
        open={dialogs.tempPassword}
        onClose={() => setDialogs(prev => ({ ...prev, tempPassword: false }))}
        title={tempPasswordData.title}
        description={tempPasswordData.description}
        password={tempPasswordData.password}
        userId={tempPasswordData.userId}
      />
      <ActionConfirmDialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmLabel={confirmDialog.confirmLabel}
        confirmColor={confirmDialog.confirmColor}
      />
    </Box>

  );
};

const paperStyles = { borderRadius: 3, overflow: "hidden", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 12px 30px rgba(2,6,23,0.06)" };
const toolbarStyles = { backgroundColor: "#3857a6", color: "#fff", px: 2.5, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" };
const tableTitleStyles = { fontFamily: "Montserrat-Bold", fontSize: "1rem" };
const secondaryCreateBtnStyles = { textTransform: "none", borderRadius: 2, color: "#fff", borderColor: "rgba(255,255,255,0.7)", "&:hover": { borderColor: "#fff", backgroundColor: "rgba(255,255,255,0.08)" } };

export default GestorUsuarios;
